import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Grid, Select, Space, Input } from "antd";

import moment from "moment";
import { useSelector } from "react-redux";
import Charts from "./Charts.index";
import GoodFaithsList from "./GoodFaithsList";
import SegmentButton from "./components/SegmentButton";
import debounce from "lodash/debounce";

import ChartFilterTags from "./components/ChartFilterTags";
import DateRangePickerCombo, { DateRangePickerMode } from "components/DateRangePickerCombo";
import GFEFilterTags from "./components/GFEFilterTags";

import styles from "./Charts.module.scss"
import { CloseCircleOutlined } from "@ant-design/icons";
import ChartFilter from "./components/ChartFilter";
import GFEFilter from "./components/GFEFilter";

const { useBreakpoint } = Grid;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10

const Tab = {
  CHARTS: "Charts",
  GFES: "GFE"
}
const ShouldMatchDates = true

export default function ChartsAndGFEs() {
  const screens = useBreakpoint();

  const isSmallDevice = !screens.md;

  const containerRef = useRef()

  const [chartPage, setChartPage] = React.useState({
    page: DEFAULT_PAGE,
    pagesize: DEFAULT_PAGE_SIZE
  }); // chart page

  const [gfePage, setGfePage] = React.useState({
    page: DEFAULT_PAGE,
    pagesize: DEFAULT_PAGE_SIZE
  }); // gfe page

  const [gfeDates, setGfeDates] = React.useState([moment(), moment()]); // gfeDate date
  const [gfeDateRangeMode, setGfeDateRangeMode] = useState(DateRangePickerMode.datepicker)
  const [chartDates, setChartDates] = React.useState([moment(), moment()]); // chart date range
  const [chartDateRangeMode, setChartDateRangeMode] = useState(DateRangePickerMode.datepicker)

  const [sorting, setSorting] = React.useState("pending");
  const [gfeSorting, setGfeSorting] = React.useState("pending");

  const [gfeFilters, setGfeFilters] = React.useState({
    stateId: undefined,
    nursePhysician1Ids: undefined,
    nursePhysician2Ids: undefined,
    status: undefined

  });
  const [chartFilters, setChartFilters] = React.useState({
    stateId: undefined,
    staffId: undefined,
    physicianId: undefined,
    chartStatus: undefined,
  });

  const currentStaff = useSelector((state) => state.user.currentUser);
  const allRoles = useSelector((state) => state.roles.allRoles);
  const hasGFEPermission = React.useMemo(() => {
    const role = allRoles?.find?.(
      (role) => role.roleId === currentStaff?.roleId
    );
    if (
      role?.permissions?.find?.(
        (permission) => permission.permissionName === "GFE Signature"
      )
    ) {
      return true;
    }
    return false;
  }, [allRoles, currentStaff]);

  const [searchText, setSearchText] = React.useState("");
  const [searchGFEInput, setSearchGFEInput] = React.useState("");
  const [searchGFEText, setSearchGFEText] = React.useState("");

  const [tab, setTab] = React.useState(Tab.CHARTS);

  const debouncedChange = React.useMemo(() =>
    debounce((value) => {
      setSearchGFEText(value);
      setGfePage(prev => ({
        ...prev,
        page: DEFAULT_PAGE,
      }));
    }, 1000)
    , []);


  const sortDropdown = useMemo(() => {
    return <div
      style={{
        display: "flex",
        justifyContent: isSmallDevice ? "space-between" : "center",
        gap: 12,
      }}
    >

      <Space className="">
        <span className="label">Sort By: </span>
        {tab === Tab.CHARTS ? (
          <Select
            className={styles.sortDropdown}
            // className="locationSelector"
            // dropdownClassName="locationSelectorDropdown"
            placeholder="Select"
            optionFilterProp="children"
            value={sorting}
            onChange={(val) => {
              setSorting(val);
            }}
            options={[
              { label: "Pending", value: "pending" },
              { label: "Signed off", value: "signedOff" },
              { label: "Newest to oldest", value: "newestToOldest" },
              { label: "Oldest to newest", value: "oldestToNewest" },
            ]}
          />
        ) : (
          <Select
            className={styles.sortDropdown}

            // className="locationSelector"
            // dropdownClassName="locationSelectorDropdown"
            placeholder="Select"
            optionFilterProp="children"
            onChange={(val) => {
              setGfeSorting(val);
            }}
            value={gfeSorting}
            options={[
              { label: "Pending", value: "pending" },
              { label: "Signed off", value: "signedOff" },
              { label: "Newest to oldest", value: "newestToOldest" },
              { label: "Oldest to newest", value: "oldestToNewest" },
            ]}
          />
        )}

      </Space>
    </div>
  }, [isSmallDevice, tab, sorting, gfeSorting])


  const hasFilterValue = useMemo(() => {

    if (tab === Tab.CHARTS && searchText?.length > 0)
      return true
    if (tab === Tab.GFES && searchGFEText?.length > 0)
      return true

    const dateFormat = 'l' // 9/25/2025
    const todayDateFormatted = moment().format(dateFormat)
    switch (tab) {
      case Tab.CHARTS:
        return Object.keys(chartFilters).find(key => {
          // match filter values
          if (chartFilters[key] !== undefined && chartFilters[key] !== null)
            return true
          // match date mode
          else if (ShouldMatchDates && (chartDateRangeMode !== DateRangePickerMode.datepicker)) {
            return true
          }
          // match dates 
          else if (ShouldMatchDates && (chartDates?.[0]?.format?.(dateFormat) !== todayDateFormatted
            || chartDates?.[1]?.format?.(dateFormat) !== todayDateFormatted)
          ) {
            return true
          }
          return false
        })
      case Tab.GFES:
        return Object.keys(gfeFilters).find(key => {
          if (gfeFilters[key] !== undefined && gfeFilters[key] !== null)
            return true
          // match date mode
          else if (ShouldMatchDates && (gfeDateRangeMode !== DateRangePickerMode.datepicker)) {
            return true
          }
          // match dates 
          else if (ShouldMatchDates && (gfeDates?.[0]?.format?.(dateFormat) !== todayDateFormatted
            || gfeDates?.[1]?.format?.(dateFormat) !== todayDateFormatted)
          ) {
            return true
          }
          return false
        })

      default:
        return false
    }
  }, [chartFilters, gfeFilters, searchText, searchGFEText,
    chartDates, chartDateRangeMode, gfeDates, gfeDateRangeMode,
    tab])

  function handleFilterClear() {
    switch (tab) {
      case Tab.CHARTS:
        setSearchText("")
        setChartFilters({
          stateId: undefined,
          staffId: undefined,
          physicianId: undefined,
          chartStatus: undefined,
        })
        setChartPage(pagination => ({
          ...pagination,
          page: DEFAULT_PAGE
        }))
        setChartDates([moment(), moment()])
        setChartDateRangeMode(DateRangePickerMode.datepicker)
        break;
      case Tab.GFES:
        setSearchGFEInput("");
        setSearchGFEText("");
        setGfeFilters({
          stateId: undefined,
          nursePhysician1Ids: undefined,
          nursePhysician2Ids: undefined,
          status: undefined
        })
        setGfePage(pagination => ({
          ...pagination,
          page: DEFAULT_PAGE
        }))
        setGfeDates([moment(), moment()])
        setGfeDateRangeMode(DateRangePickerMode.datepicker)
        break;
      default:
        break;
    }
  }


  useLayoutEffect(() => {

    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });

    }
  }, [chartPage, gfePage])


  return (
    <div style={{
      // fallback height
      height: isSmallDevice
        ? "calc(100vh - 42px)"
        : "calc(100vh -  80px)",
      height: isSmallDevice
        ? "calc(100dvh - 42px)"
        : "calc(100dvh -  80px)",
      background: "#e5e8ee",
      overflow: 'auto',
      position: 'relative'
    }}
      ref={containerRef}
    >
      <div style={{ position: 'sticky' }}>
        <div
          style={{
            height: "auto",
            background: "#e5e8ee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: isSmallDevice ? "column" : "row",
            paddingBottom: isSmallDevice ? 0 : 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              // marginBottom: isSmallDevice ? 4 : 0,
              justifyContent: isSmallDevice ? 'space-between' : 'flex-start',
              flexWrap: "wrap",
              marginBottom: 16
            }}
          >
            <SegmentButton
              options={hasGFEPermission ? [Tab.CHARTS, Tab.GFES] : [Tab.CHARTS]}
              selected={tab}
              onChange={(value) => setTab(value)}
            />
            {tab === Tab.CHARTS && (
              <div>
                <DateRangePickerCombo
                  dates={chartDates}
                  setDates={setChartDates}
                  mode={chartDateRangeMode}
                  setMode={setChartDateRangeMode}
                />
              </div>
            )}

            {tab === Tab.GFES && (
              <div>
                <DateRangePickerCombo
                  dates={gfeDates}
                  setDates={setGfeDates}
                  mode={gfeDateRangeMode}
                  setMode={setGfeDateRangeMode}
                />
              </div>
            )}
            {tab === Tab.CHARTS && (
              <Input.Search
                className={"toolbarSearchInput"}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setChartPage(prev => ({
                    ...prev,
                    page: DEFAULT_PAGE,
                  }))
                }}
                placeholder={"Search"}
                allowClear
              />
            )}
            {tab === Tab.GFES && (
              <Input.Search
                className={"toolbarSearchInput"}
                value={searchGFEInput}
                onChange={(e) => {
                  setSearchGFEInput(e.target.value);
                  debouncedChange(e.target.value);
                }}
                placeholder={"Search"}
                // onSearch={onSearch}
                allowClear
              />
            )}
            {
              tab === Tab.CHARTS && <ChartFilter
                selectedNP={chartFilters}
                onSelectedNPChange={(filters) => {
                  setChartFilters(filters)
                  setChartPage(prev => ({
                    ...prev,
                    page: DEFAULT_PAGE,
                  }));
                }}

              />
            }
            {
              tab === Tab.GFES && <GFEFilter
                selectedNP={gfeFilters}
                onSelectedNPChange={(filters) => {
                  setGfeFilters(filters)
                  setGfePage(prev => ({
                    ...prev,
                    page: DEFAULT_PAGE
                  }))
                }}
              />
            }
            {
              hasFilterValue && <div
                onClick={handleFilterClear}
                className={styles.clearFilters}
              >
                <CloseCircleOutlined />
                Clear
              </div>
            }

            {
              isSmallDevice && sortDropdown
            }
          </div>

          {
            !isSmallDevice && sortDropdown
          }
        </div>

        {tab === Tab.CHARTS ? (
          <Charts dates={chartDates} sorting={sorting} npFilter={chartFilters} searchText={searchText}
            page={chartPage}
            setPage={setChartPage}
          />
        ) : (
          <GoodFaithsList
            page={gfePage}
            setPage={setGfePage}
            isSmallDevice={isSmallDevice}
            searchText={searchGFEText || ""}
            sorting={gfeSorting === "All" ? undefined : gfeSorting}
            date={gfeDates}
            npFilter={gfeFilters}
          />
        )}
      </div>
    </div>
  );
}
